import { ChromeOutlined, DeleteOutlined, FilterOutlined, MoreOutlined, PlayCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { useAuthHeaders } from "@src/frontend/hooks/useAuthHeaders";
import { usePermission } from "@src/frontend/hooks/usePermission";
import { useAgentStore } from "@src/frontend/stores/agentStore";
import { useSessionStore } from "@src/frontend/stores/sessionStore";
import { LogtoPermission } from "@src/utils/constants";
import { App, Button, Dropdown, Flex, Input, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/lib/table";
import { useCallback, useMemo, useRef, useState } from "react";
import StopCircleIcon from '@mui/icons-material/StopCircle';
import styled from "styled-components";
import { useRestAPI } from "@src/frontend/hooks/useRestAPI";
import type { BrowserSessionModel } from "@src/generated/prisma/models";
import { ActionExecutor, type ActionExecutorHandler } from "@src/frontend/components/ActionExecutor";
import { ConnectedTvOutlined, TerminalOutlined } from "@mui/icons-material";
import { useScriptOptions } from "@src/frontend/hooks/useScriptOptions";
import { ViewScriptResultBtn } from "@src/frontend/components/ViewScriptResultBtn";

const StyledComponent = styled(Flex)``;


export const SessionList = () => {
  const [name, setName] = useState('');
  const { getAuthHeader } = useAuthHeaders();
  const { hasPermission } = usePermission();
  const { listReq, updateReq, createReq, delReq: deleteReq } = useRestAPI<BrowserSessionModel>('/api/sessions');
  const adminColumns = useMemo<ColumnsType<BrowserSessionModel>>(() => {
    if (!hasPermission(LogtoPermission.ADMIN)) return [];
    const filters = new Set(listReq.data?.data?.map(e => e.logto_uid)).values().map(e => ({ text: e, value: e })).toArray();
    return [
      {
        width: 220, dataIndex: 'logto_uid', align: 'center', title: 'Logto UID',
        filters,
        ellipsis: true,
        filterIcon: <FilterOutlined />,
        onFilter: (value, record) => record.logto_uid === value,
      },
    ];
  }, [hasPermission, listReq]);
  const handleAdd = useCallback(async () => {
    const res = await createReq.runAsync({ name });
    if (!res.ok) return;
    setName('');
    await listReq.runAsync();
  }, [name, createReq, listReq]);
  const { message, modal } = App.useApp();
  const handleRunAt = useCallback(async (session_id: string, agent_id: string | null, old_agent_id: string | null) => {
    console.debug('Run at', { session_id, agent_id, oldAgent: old_agent_id });
    const headers = await getAuthHeader();
    if (!headers) return;
    await fetch(`/api/sessions/${agent_id}/${session_id}`, {
      headers,
      method: 'PUT',
    });
    message.success('Session start');
    await listReq.runAsync();
  }, [getAuthHeader, listReq]);
  const handleStop = useCallback(async (session_id: string, agent_id: string) => {
    const headers = await getAuthHeader();
    if (!headers) return;
    await fetch(`/api/sessions/${agent_id}/${session_id}`, {
      headers,
      method: 'DELETE',
    });
    await listReq.runAsync();
    message.success('Session stoped');
  }, [getAuthHeader, listReq]);
  const {
    agents,
    online,
    nameMap: agentNameMap,
  } = useAgentStore(data => data);
  const { sessions } = useSessionStore();
  const executor = useRef<ActionExecutorHandler>(null);
  const items = useScriptOptions();
  return (
    <StyledComponent vertical gap={12}>
      <Typography.Title level={3}>
        <Flex gap={8} align="center">
          <span>浏览器会话</span>
          <Button onClick={listReq.runAsync}><SyncOutlined /></Button>
        </Flex>
      </Typography.Title>
      <Flex gap={12}>
        <Input
          prefix="名称："
          placeholder="账号 001"
          value={name}
          onChange={e => setName(e.target.value)}
          onPressEnter={handleAdd}
        />
        <Button
          disabled={!name}
          icon={<ChromeOutlined />}
          onClick={handleAdd}
        >创建会话</Button>
      </Flex>
      <Table
        size="small"
        loading={listReq.loading}
        dataSource={listReq.data?.data}
        rowKey={e => e.id}
        scroll={{ x: 900 }}
        columns={[
          { width: 300, dataIndex: 'name', title: '浏览器会话', render: (name, { id }) => {
            return (<Flex vertical gap={2}><Typography.Text strong editable={{
                async onChange(value) {
                  await updateReq.runAsync({ name: value }, id);
                  await listReq.runAsync();
                },
              }}>{name}</Typography.Text><Typography.Text type="secondary" copyable={{ text: id }} ellipsis>{id}</Typography.Text></Flex>);
          } },
          ...adminColumns,
          { width: 180, dataIndex: 'agent_id', title: '所属节点', render: id => agentNameMap[id] || '未分配' },
          { width: 120, dataIndex: 'id', title: '状态', render: id => sessions.includes(id) ? <Tag color="success">运行中</Tag> : <Tag>未启动</Tag> },
          { fixed: 'right', width: 300, align: 'right', dataIndex: 'id', title: '操作', render: (id, { agent_id, name }) => {
            const active = sessions.includes(id);
            const agent = (agent_id && agents.find(e => e.id === agent_id)) || agents.find(e => online[e.id]);
            const openScreen = () => {
              const params = new URLSearchParams({ session_id: id, agent_id: agent?.id ?? '' });
              open(`/sessions/screen?${params}`, '_blank', "left=100,top=100,width=1024,height=768");
            };
            return <Flex gap={8} justify="flex-end">
              {active ? <Button icon={<ConnectedTvOutlined />} onClick={openScreen}>查看画面</Button> : <Button type="primary" icon={<PlayCircleOutlined />} disabled={!agent || !online[agent.id]} onClick={() => agent && handleRunAt(id, agent.id, agent_id)}>启动</Button>}
              <Dropdown disabled={!active} menu={{ items, onClick: e => executor.current?.open({ agent_id: agent_id ?? '', session_ids: [id], script_id: e.key }) }}>
                <Button disabled={!active} icon={<TerminalOutlined />} onClick={() => executor.current?.open({ agent_id: agent_id ?? '', session_ids: [id] })}>执行</Button>
              </Dropdown>
              <Dropdown menu={{ items: [
                { key: 'result', label: '查看运行记录' },
                ...(active ? [{ key: 'stop', label: '停止会话' }] : []),
                ...agents.filter(e => e.id !== agent?.id).map(e => ({ key: `agent:${e.id}`, label: `切换到 ${e.name}`, disabled: !online[e.id] })),
                { type: 'divider' as const }, { key: 'delete', danger: true, label: '删除会话', icon: <DeleteOutlined /> },
              ], onClick: async ({ key }) => {
                if (key === 'stop' && agent_id) await handleStop(id, agent_id);
                if (key.startsWith('agent:')) await handleRunAt(id, key.slice(6), agent_id);
                if (key === 'result') open(`/script-result?session_id=${id}`, '_self');
                if (key === 'delete') modal.confirm({ title: `删除会话「${name}」？`, content: '相关浏览器数据将被清除，此操作无法恢复。', okText: '删除', okButtonProps: { danger: true }, onOk: async () => { await deleteReq.runAsync(id); await listReq.runAsync(); } });
              } }}><Button icon={<MoreOutlined />} /></Dropdown>
            </Flex>;
          } },
        ]}
      />
      <ActionExecutor ref={executor} />
    </StyledComponent>
  );
}
